import { Link2, MessageCircle, Instagram, Send, Bot, Workflow, Brain, CreditCard, Webhook, Sparkles } from "lucide-react";
import { motion } from "framer-motion";

// Using Lucide icons instead of external URLs to avoid 403/404 errors and improve performance
const channels = [
  {
    name: "WhatsApp Oficial",
    icon: MessageCircle,
    category: "Canal",
    color: "text-success-green",
  },
  {
    name: "WhatsApp QR-Code",
    icon: MessageCircle,
    category: "Canal",
    color: "text-success-green",
  },
  {
    name: "Instagram Direct",
    icon: Instagram,
    category: "Canal",
    color: "text-pink-500",
  },
  {
    name: "Facebook Messenger",
    icon: MessageCircle,
    category: "Canal",
    color: "text-blue-500",
  },
  {
    name: "Telegram",
    icon: Send,
    category: "Canal",
    color: "text-sky-500",
  },
];

const integrations = [
  {
    name: "Typebot",
    icon: Bot,
    category: "Chatbot",
    color: "text-orange-500",
  },
  {
    name: "Dify.ai",
    icon: Brain,
    category: "IA",
    color: "text-purple-500",
  },
  {
    name: "n8n",
    icon: Workflow,
    category: "Automação",
    color: "text-red-500",
  },
  {
    name: "Dialogflow",
    icon: Bot,
    category: "Chatbot",
    color: "text-yellow-500",
  },
  {
    name: "Wavoip",
    icon: MessageCircle,
    category: "Telefonia",
    color: "text-blue-400",
  },
  {
    name: "Asaas",
    icon: CreditCard,
    category: "Pagamentos",
    color: "text-green-500",
  },
  {
    name: "Webhooks",
    icon: Webhook,
    category: "API",
    color: "text-gray-500",
  },
];

const aiIntegrations = [
  {
    name: "ChatGPT",
    icon: Sparkles,
    category: "IA",
    color: "text-emerald-500",
  },
  {
    name: "Gemini",
    icon: Sparkles,
    category: "IA",
    color: "text-blue-500",
  },
  {
    name: "Claude",
    icon: Sparkles,
    category: "IA",
    color: "text-orange-400",
  },
  {
    name: "Deepseek",
    icon: Brain,
    category: "IA",
    color: "text-indigo-500",
  },
  {
    name: "Ollama",
    icon: Brain,
    category: "IA",
    color: "text-slate-600",
  },
  {
    name: "LM Studio",
    icon: Brain,
    category: "IA",
    color: "text-violet-500",
  },
];

const IntegrationsSection = () => {
  return (
    <section id="integracoes" className="py-16 md:py-24 relative overflow-hidden" aria-labelledby="integrations-heading">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-surface-card/30 via-transparent to-surface-card/30" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <motion.div 
          className="text-center max-w-3xl mx-auto mb-12 md:mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-6">
            <Link2 size={18} className="text-electric-purple" aria-hidden="true" />
            <span className="text-sm text-foreground/70">Conecte tudo</span>
          </div>
          <h2 id="integrations-heading" className="text-2xl md:text-4xl lg:text-5xl font-display font-bold mb-4 md:mb-6">
            Integra com suas{" "}
            <span className="text-gradient-zavvi">ferramentas favoritas</span>
          </h2>
          <p className="text-base md:text-lg text-foreground/70 px-4">
            Conecte múltiplos canais de atendimento, IAs generativas e ferramentas de automação em um único sistema.
          </p>
        </motion.div>

        {/* Channels Section */}
        <motion.div 
          className="mb-10 md:mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <h3 className="text-center text-lg md:text-xl font-semibold mb-6 text-foreground">
            Canais de Atendimento
          </h3>
          <div className="flex flex-wrap justify-center gap-3 md:gap-4 max-w-4xl mx-auto">
            {channels.map((channel, index) => {
              const IconComponent = channel.icon;
              return (
                <motion.div
                  key={channel.name}
                  className="glass-card px-4 py-3 md:px-6 md:py-4 rounded-xl flex items-center gap-3 hover:scale-105 transition-transform min-h-[48px]"
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <IconComponent 
                    size={24} 
                    className={channel.color}
                    aria-hidden="true"
                  />
                  <span className="text-xs md:text-sm font-medium text-foreground">{channel.name}</span>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Integrations Grid */}
        <motion.div 
          className="mb-10 md:mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <h3 className="text-center text-lg md:text-xl font-semibold mb-6 text-foreground">
            Integrações Nativas
          </h3>
          <div className="flex flex-wrap justify-center gap-3 md:gap-4 max-w-5xl mx-auto">
            {integrations.map((integration, index) => {
              const IconComponent = integration.icon;
              return (
                <motion.div
                  key={integration.name}
                  className="glass-card px-4 py-3 md:px-5 md:py-4 rounded-xl flex items-center gap-3 hover:scale-105 hover:neon-border-cyan transition-all duration-300 min-h-[48px]"
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <IconComponent 
                    size={20} 
                    className={integration.color}
                    aria-hidden="true"
                  />
                  <div className="flex flex-col">
                    <span className="text-xs md:text-sm font-medium text-foreground">{integration.name}</span>
                    <span className="text-[10px] md:text-xs text-foreground/60">{integration.category}</span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* AI Integrations */}
        <motion.div 
          className="mb-10 md:mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <h3 className="text-center text-lg md:text-xl font-semibold mb-6 text-foreground">
            Agentes de I.A.
          </h3>
          <div className="flex flex-wrap justify-center gap-3 md:gap-4 max-w-4xl mx-auto">
            {aiIntegrations.map((ai, index) => {
              const IconComponent = ai.icon;
              return (
                <motion.div
                  key={ai.name}
                  className="glass-card px-4 py-3 md:px-5 md:py-4 rounded-xl flex items-center gap-3 hover:scale-105 hover:neon-border-purple transition-all duration-300 min-h-[48px]"
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <IconComponent 
                    size={20} 
                    className={ai.color}
                    aria-hidden="true"
                  />
                  <span className="text-xs md:text-sm font-medium text-foreground">{ai.name}</span>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Categories */}
        <motion.div 
          className="flex flex-wrap justify-center gap-2 md:gap-3 mt-8 md:mt-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          {["WhatsApp", "Instagram", "Facebook", "Telegram", "Chatbots", "Automação", "IA Generativa", "Pagamentos"].map((category) => (
            <span
              key={category}
              className="px-3 py-2 md:px-4 md:py-2 rounded-full glass-card text-xs md:text-sm text-foreground/70 hover:text-foreground hover:border-cyber-cyan/30 transition-colors cursor-default min-h-[40px] flex items-center"
            >
              {category}
            </span>
          ))}
        </motion.div>

      </div>
    </section>
  );
};

export default IntegrationsSection;
